import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDepartment, useDepartments } from '@/api/queries/useDepartments';
import { notFoundOrUnknownKey } from '@/api/errors';
import { useOrgMembers } from '@/api/queries/useOrgMembers';
import { useDepartmentTeams } from '@/api/queries/useOrgFunctions';
import { useAuthStore } from '@/stores/auth-store';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BackButton } from '@/components/back-button';
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Skeleton,
  Page,
  PageContent,
  UserCard,
  DataTable,
  createColumnHeader,
  formatDate,
  renderFallback,
  type DataTableColumnDef
} from '@bliks-io/ui';
import {
  type Department,
  type ListOrgFunctionsItem,
  type PopulatedUser
} from '@bliks-io/contracts';
import {
  buildOrgFunctionNameColumn,
  buildOrgFunctionLeadColumn,
  buildOrgFunctionMemberCountColumn
} from '@/pages/organization/org-functions/utils/orgFunctionColumns';

interface DepartmentDetailPageProps {
  departmentId: string;
}

export function DepartmentDetailPage({ departmentId }: DepartmentDetailPageProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const { data: department, isPending, error } = useDepartment(departmentId);
  const { data: departments, isLoading: departmentsLoading } = useDepartments(); // Get all departments for parent lookup
  const { data: members = [], isLoading: membersLoading } = useOrgMembers({ departmentId });
  const { data: departmentOrgFunctions, isLoading: orgFunctionsLoading } =
    useDepartmentTeams(departmentId);

  useDocumentTitle('pageTitles.departmentDetail', { name: department?.name || 'Loading...' });

  // Column definitions for the members table
  const memberColumns = useMemo<DataTableColumnDef<PopulatedUser>[]>(
    () => [
      {
        id: 'name',
        accessorFn: (row) => `${row.firstName} ${row.lastName}`,
        header: createColumnHeader(t('common.Name')),
        cell: ({ row }) => (
          <UserCard
            user={{
              firstName: row.original.firstName,
              lastName: row.original.lastName,
              email: row.original.email,
              avatar: row.original.avatar,
              role: row.original.mainRole,
              departmentAbbreviation: department?.abbreviation
            }}
            variant="with-role"
          />
        )
      },
      {
        accessorKey: 'email',
        header: createColumnHeader(t('common.Email'))
      }
    ],
    [t, department?.abbreviation]
  );

  const orgFunctionColumns = useMemo<DataTableColumnDef<ListOrgFunctionsItem>[]>(
    () => [
      buildOrgFunctionNameColumn(t, slug),
      buildOrgFunctionLeadColumn(t),
      buildOrgFunctionMemberCountColumn(t)
    ],
    [t, slug]
  );

  // Helper function to calculate level from path
  const calculateLevel = (path: string | undefined): number => {
    if (!path) return 1;
    const segments = path.split('#');
    return segments.length;
  };

  // Helper function to get parent department name from path
  const getParentDepartmentName = (path: string | undefined): string => {
    if (!path || !departments) return '-';
    const segments = path.split('#');

    // If there's only one segment, it's a root department (no parent)
    if (segments.length === 1) return '-';

    // Get the parent ID (second to last segment)
    const parentId = segments[segments.length - 2];

    // Find the parent department in the list
    const parentDept = departments.find((d: Department) => d._id === parentId);
    return parentDept?.name || '-';
  };

  if (isPending) {
    return (
      <Page>
        <PageContent>
          <Skeleton className="w-64 h-8 mb-8" />
          <Card>
            <CardHeader>
              <Skeleton className="w-48 h-6" />
            </CardHeader>
            <CardContent className="space-y-4">
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-full h-4" />
              <Skeleton className="w-2/3 h-4" />
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  if (error || !department) {
    return (
      <Page>
        <PageContent>
          <div className="flex items-center gap-4 mb-8">
            <BackButton to="/$slug/organization/departments" params={{ slug }} />
          </div>
          <Card>
            <CardContent className="p-6">
              <p className="text-destructive">
                {t(notFoundOrUnknownKey(error, 'organization.departments.notFound'))}
              </p>
            </CardContent>
          </Card>
        </PageContent>
      </Page>
    );
  }

  return (
    <Page>
      <PageContent>
        <div className="flex items-center gap-4 mb-8">
          <BackButton to="/$slug/organization/departments" params={{ slug }} />
          <div>
            <h1 className="text-xl font-semibold leading-7">{department.name}</h1>
            <p className="mt-1 text-sm font-normal leading-5 text-gray-8">
              {t('organization.departments.detailDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.departments.details')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Name')}</label>
                <p className="mt-1 text-base">{department.name}</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organizations.Level')}
                  </label>
                  <p className="mt-1 text-base">{calculateLevel(department.path)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">
                    {t('organizations.Abbreviation')}
                  </label>
                  <p className="mt-1 text-base">{renderFallback(department.abbreviation)}</p>
                </div>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">{t('common.Description')}</label>
                <p className="mt-1 text-base">{renderFallback(department.description)}</p>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organizations.ParentDepartment')}
                </label>
                {departmentsLoading && calculateLevel(department.path) > 1 ? (
                  <Skeleton className="mt-1 h-6 w-40" />
                ) : (
                  <p className="mt-1 text-base">{getParentDepartmentName(department.path)}</p>
                )}
              </div>

              <div>
                <label className="text-sm font-medium text-gray-8">
                  {t('organization.departments.headOfDepartment')}
                </label>
                <div className="mt-1">
                  {department.manager ? (
                    <UserCard
                      user={{
                        firstName: department.manager.firstName,
                        lastName: department.manager.lastName,
                        email: department.manager.email,
                        avatar: department.manager.avatar,
                        role: department.manager.mainRole,
                        departmentAbbreviation: department.abbreviation
                      }}
                      variant="with-role"
                    />
                  ) : (
                    renderFallback(undefined)
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Created')}</label>
                  <p className="mt-1 text-base">{formatDate(department.createdAt)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-8">{t('common.Updated')}</label>
                  <p className="mt-1 text-base">{formatDate(department.updatedAt)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('organization.departments.members')}</CardTitle>
            </CardHeader>
            <CardContent>
              {membersLoading ? (
                <div className="space-y-2">
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                  <Skeleton className="h-12 w-full" />
                </div>
              ) : (
                <DataTable
                  columns={memberColumns}
                  data={members}
                  emptyMessage={t('organization.departments.noMembersFound')}
                  enableGlobalFilter
                  globalSearchPlaceholder={t('organization.departments.searchMembers')}
                />
              )}
            </CardContent>
          </Card>

          {/* useDepartmentTeams narrows to team-kind, so the card is a Teams
              card — not the role-kind „Funktionen" catalog. */}
          <Card>
            <CardHeader>
              <CardTitle>{t('organization.departments.teams')}</CardTitle>
            </CardHeader>
            <CardContent>
              <DataTable
                columns={orgFunctionColumns}
                data={departmentOrgFunctions}
                isLoading={orgFunctionsLoading}
                emptyMessage={t('organization.departments.noTeamsFound')}
                enableGlobalFilter
                globalSearchPlaceholder={t('organization.departments.searchTeams')}
              />
            </CardContent>
          </Card>
        </div>
      </PageContent>
    </Page>
  );
}
